import {useCallback} from 'preact/hooks';

import {useI18n} from '~/foundation/I18n/hooks';

import {useInstallments} from '../context/InstallmentsContext';
import {useLegalLinks} from '../hooks/useLegalLinks';
import {usePaymentTermsMonorail} from '../monorail';
import {getLegalCopyKeys} from '../utils/getModalText';

import {ContinueToCheckoutButton} from './ContinueToCheckoutButton';
import {InstallmentsModalFooter} from './InstallmentsModalFooter';
import {SamplePlanList} from './SamplePlansList';

export const SamplePlansModalContent = () => {
  const {
    countryCode,
    isModalOpen,
    metaType,
    modalToken,
    variantInfo,
    checkoutUrl,
    loanTypes,
    showNewBuyerIncentive,
  } = useInstallments();
  const {translate} = useI18n();
  const {trackModalAction} = usePaymentTermsMonorail();
  const legalLinks = useLegalLinks();

  const getLegalCopy = () => {
    switch (countryCode) {
      case 'GB': {
        const legalCopyKeys = getLegalCopyKeys({
          countryCode,
          // Empty fragment as lendersLink is not used for GB
          lendersLink: <></>,
          // modalType doesn't affect GB logic
          modalType: 'split_pay',
          loanTypes,
        });
        const text = legalCopyKeys
          .map((key) =>
            typeof key === 'string'
              ? translate(key)
              : translate(key[0], key[1]),
          )
          .filter(Boolean)
          .join('\n\n');
        return text;
      }
      default:
        return translate(
          `paymentTerms.samplePlansContent.legal.${countryCode}`,
          legalLinks,
        );
    }
  };

  const handleContinueToCheckout = useCallback(() => {
    if (variantInfo?.available === false) return;
    trackModalAction({
      modalToken,
      action: 'continue_to_checkout',
    });
  }, [modalToken, trackModalAction, variantInfo]);

  return (
    <div data-testid="sample-plans-modal-content">
      <SamplePlanList />
      {metaType !== 'checkout' && checkoutUrl && (
        <>
          {isModalOpen && (
            <div
              data-testid="sample-plans-modal-continue-to-checkout-button"
              className="mt-4 w-full"
              onClick={handleContinueToCheckout}
            >
              <ContinueToCheckoutButton />
            </div>
          )}
          <div className="my-1 flex flex-col items-center text-caption font-light leading-normal">
            {countryCode !== 'GB' && (
              <>
                <p className="text-center font-light">
                  {translate(
                    'paymentTerms.samplePlansContent.informationShared',
                  )}
                </p>
                <p className="text-center font-light">
                  {translate(
                    'paymentTerms.samplePlansContent.checkingEligibility',
                  )}
                </p>
              </>
            )}
          </div>
        </>
      )}
      <p
        id="eligibility-approval"
        data-testid="eligibility-approval"
        className={`mb-5 mt-4 text-caption font-light tracking-wider text-grayscale-d0 ${
          countryCode === 'GB' ? 'whitespace-pre-line' : ''
        }`}
      >
        {showNewBuyerIncentive && countryCode === 'US' && (
          <>
            {translate(
              'paymentTerms.samplePlansContent.legal.newBuyerIncentive',
            )}{' '}
          </>
        )}
        {getLegalCopy()}
      </p>
      <InstallmentsModalFooter />
    </div>
  );
};
